import { differenceInYears, isValid } from 'date-fns'

import { Form, LONG_BDAY_FORMAT, formatBirthday } from '../../../ana-toolbox'
import { toBoolean } from '../helpers/toBoolean'

export type Type = Form

export const toPost = (form: Partial<Form>): Partial<Form> => {
  const birthday = formatBirthday(form.birthday || '')
  const holderBirthday = formatBirthday(form.holderBirthday || '')

  return {
    ...form,
    diseases: form.diseases || [],
    isPregnant: toBoolean(form.isPregnant),
    pregnantMonths: form.pregnantMonths && form.pregnantMonths > 0 ? Number(form.pregnantMonths) : undefined,
    signatureEncoded: form.signatureEncoded?.replace('data:image/png;base64,', ''),
    birthday,
    holderBirthday,

    /* The e-mail address is optional, so an empty one is left out of the payload entirely: the BE
     * keeps the address it already has for the patient when it receives no value, while sending
     * `''` would wipe it (D21-41541). Leaving the field empty therefore cannot clear a stored
     * address - existing wrong addresses are removed by the BE clean-up (D21-41561), not from here.
     */
    email: form.email || undefined,

    /* We need to remove the `company` and `dentalInsuranceCompany` fields as these fields holds the
     * name of the insurance company and is not accepted by the BE when the reference id is present.
     */
    insuranceCompany: form.insuranceCompanyId ? undefined : form.insuranceCompany,
    dentalInsuranceCompany: form.dentalInsuranceCompanyId ? undefined : form.dentalInsuranceCompany,

    hasFamilyInsurance: form.hasFamilyInsurance ?? false,

    /*
     * We have to invert the value here because the question is
     * "Do you and the family insurance holder have the same address?"
     * but the field is "livesOnDifferentAddress", which is the opposite.
     */
    livesOnDifferentAddress: form.livesOnDifferentAddress ?? false,
  }
}

export const isPatientUnderaged = (form: Partial<Form>): boolean => {
  const trimmedValue = form.birthday?.trim() || ''

  if (!form?.birthday) return false

  try {
    const birthday =
      trimmedValue.length === LONG_BDAY_FORMAT.length ? new Date(formatBirthday(form?.birthday)) : undefined

    if (!isValid(birthday) || !birthday) return false

    return differenceInYears(new Date(), birthday) < 18
  } catch {
    return false
  }
}

/*
 * Returns whether or not the anamnesis has data about the family insurance (holderFirstName, holderLastName, etc...)
 */
export const hasFamilyInsuranceData = (form: Partial<Form>): boolean => {
  return (
    Boolean(form.holderFirstName) ||
    Boolean(form.holderLastName) ||
    Boolean(form.holderCity) ||
    Boolean(form.holderStreet) ||
    Boolean(form.holderZipcode) ||
    Boolean(form.holderBirthday)
  )
}

export const hasLegalRepresentativeName = (form: Partial<Form>): boolean =>
  Boolean(form.contactPersonFirstName && form.contactPersonLastName)

export const getSignerName = (form: Partial<Form>): string => {
  const nameParts = hasLegalRepresentativeName(form)
    ? [form.contactPersonFirstName, form.contactPersonLastName]
    : [form.firstName, form.lastName]

  const validParts = nameParts.filter(Boolean)

  return validParts.length > 0 ? validParts.join(' ') : ''
}
